import { ref } from 'vue'
import {
  doc,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  updateDoc,
  writeBatch
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import { usePoint } from './usePoint'

// 한국 시간(KST) 가져오기
const getKoreanTime = () => {
  const now = new Date()
  const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
  const year = koreaTime.getFullYear()
  const month = String(koreaTime.getMonth() + 1).padStart(2, '0')
  const day = String(koreaTime.getDate()).padStart(2, '0')

  return {
    date: `${year}-${month}-${day}`,
    fullDate: koreaTime
  }
}

// 가격표 (기간별) - originalPrice: 정가, price: 할인가
// 2주 10,000P 기준
const CAMPAIGN_PRICING = {
  '2weeks': { label: '2주', days: 14, originalPrice: 10000, price: 10000, discount: 0 },
  '1month': { label: '1개월', days: 30, originalPrice: 20000, price: 18000, discount: 10 },
  '2months': { label: '2개월', days: 60, originalPrice: 40000, price: 32000, discount: 20 },
  '3months': { label: '3개월', days: 90, originalPrice: 60000, price: 45000, discount: 25 }
}

// 연장 가격표 (기존 캠페인에 추가)
const EXTENSION_PRICING = {
  '1week': { label: '1주', days: 7, originalPrice: 5000, price: 5000, discount: 0 },
  '2weeks': { label: '2주', days: 14, originalPrice: 10000, price: 9000, discount: 10 },
  '1month': { label: '1개월', days: 30, originalPrice: 20000, price: 16000, discount: 20 }
}

export function useCampaign() {
  const { user } = useAuth()
  const { usePoints } = usePoint()

  const myCampaigns = ref([])
  const loading = ref(false)
  const error = ref(null)

  // 가격표 반환
  const getPricing = () => CAMPAIGN_PRICING

  // 연장 가격표 반환
  const getExtensionPricing = () => EXTENSION_PRICING

  // 활성 캠페인 있는지 확인 (1인 1캠페인 제한)
  const hasActiveCampaign = async () => {
    if (!user.value) return false

    try {
      const q = query(
        collection(db, 'campaigns'),
        where('partnerId', '==', user.value.uid),
        where('status', '==', 'active')
      )
      const snapshot = await getDocs(q)

      // 만료 체크
      const { date: today } = getKoreanTime()
      for (const docSnap of snapshot.docs) {
        const campaign = docSnap.data()
        if (campaign.endDate >= today) {
          return true // 아직 유효한 활성 캠페인이 있음
        }
      }
      return false
    } catch (err) {
      console.error('활성 캠페인 체크 실패:', err)
      return false
    }
  }

  // 내 활성 캠페인 가져오기
  const getActiveCampaign = async () => {
    if (!user.value) return null

    try {
      const q = query(
        collection(db, 'campaigns'),
        where('partnerId', '==', user.value.uid),
        where('status', '==', 'active')
      )
      const snapshot = await getDocs(q)

      const { date: today } = getKoreanTime()
      for (const docSnap of snapshot.docs) {
        const campaign = { id: docSnap.id, ...docSnap.data() }
        if (campaign.endDate >= today) {
          return campaign
        }
      }
      return null
    } catch (err) {
      console.error('활성 캠페인 조회 실패:', err)
      return null
    }
  }

  // 고유 캠페인 ID 생성 (8자리 영숫자)
  const generateCampaignCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
    let code = ''
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return code
  }

  // 캠페인 생성
  const createCampaign = async (propertyId, propertyName, durationKey) => {
    if (!user.value) return { success: false, error: '로그인이 필요합니다.' }

    const pricingInfo = CAMPAIGN_PRICING[durationKey]
    if (!pricingInfo) return { success: false, error: '유효하지 않은 기간입니다.' }

    try {
      loading.value = true

      // 0. 이미 활성 캠페인이 있는지 확인
      const hasActive = await hasActiveCampaign()
      if (hasActive) {
        return { success: false, error: '이미 활성 캠페인이 있습니다. 1인 1캠페인만 운영 가능합니다.' }
      }

      // 0-1. propertyPartners에서 영업명(displayName) 조회
      let partnerDisplayName = '상담사'
      try {
        const partnerQuery = query(
          collection(db, 'propertyPartners'),
          where('partnerId', '==', user.value.uid),
          where('propertyId', '==', propertyId),
          where('status', '==', 'active')
        )
        const partnerSnapshot = await getDocs(partnerQuery)
        if (!partnerSnapshot.empty) {
          partnerDisplayName = partnerSnapshot.docs[0].data().displayName || '상담사'
        }
      } catch (err) {
        console.error('파트너 정보 조회 실패:', err)
      }

      // 1. 코인 차감
      const pointResult = await usePoints(
        pricingInfo.price,
        `캠페인 생성 - ${propertyName} (${pricingInfo.label})`
      )

      if (!pointResult.success) {
        return { success: false, error: pointResult.error }
      }

      // 2. 시작일/종료일 계산 (한국 시간 기준)
      const { fullDate: startDate } = getKoreanTime()
      const endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + pricingInfo.days)

      const formatDate = (date) => {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }

      // 3. 고유 코드 생성
      const campaignCode = generateCampaignCode()

      // 4. 캠페인 문서 생성
      const campaignDoc = await addDoc(collection(db, 'campaigns'), {
        code: campaignCode,
        propertyId,
        propertyName,
        partnerId: user.value.uid,
        partnerDisplayName,
        partnerEmail: user.value.email,

        // 기간 정보
        duration: durationKey,
        durationLabel: pricingInfo.label,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),

        // 비용
        price: pricingInfo.price,

        // 통계
        views: 0,
        consultations: 0,

        // 상태
        status: 'active',
        createdAt: new Date().toISOString(),
        expiresAt: endDate.toISOString()
      })

      return {
        success: true,
        campaignId: campaignDoc.id,
        campaignCode,
        url: `/p/${campaignCode}`
      }
    } catch (err) {
      console.error('캠페인 생성 실패:', err)
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 내 캠페인 목록 조회
  const loadMyCampaigns = async () => {
    if (!user.value) return

    try {
      loading.value = true
      const q = query(
        collection(db, 'campaigns'),
        where('partnerId', '==', user.value.uid),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      myCampaigns.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      // 만료된 캠페인 상태 업데이트
      const { date: today } = getKoreanTime()
      for (const campaign of myCampaigns.value) {
        if (campaign.status === 'active' && campaign.endDate < today) {
          await updateDoc(doc(db, 'campaigns', campaign.id), {
            status: 'expired'
          })
          campaign.status = 'expired'
        }
      }
    } catch (err) {
      console.error('캠페인 목록 로드 실패:', err)
      error.value = err.message
    } finally {
      loading.value = false
    }
  }

  // 캠페인 코드로 조회 (랜딩 페이지용)
  const getCampaignByCode = async (code) => {
    try {
      const q = query(
        collection(db, 'campaigns'),
        where('code', '==', code)
      )

      const snapshot = await getDocs(q)
      if (snapshot.empty) return null

      const campaignDoc = snapshot.docs[0]
      const campaign = {
        id: campaignDoc.id,
        ...campaignDoc.data()
      }

      // 만료 체크
      const { date: today } = getKoreanTime()
      if (campaign.endDate < today) {
        campaign.status = 'expired'
      }

      return campaign
    } catch (err) {
      console.error('캠페인 조회 실패:', err)
      return null
    }
  }

  // 캠페인 조회수 증가
  const incrementViews = async (campaignId) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)

      if (campaignDoc.exists()) {
        const currentViews = campaignDoc.data().views || 0
        await updateDoc(campaignRef, {
          views: currentViews + 1
        })
      }
    } catch (err) {
      console.error('조회수 업데이트 실패:', err)
    }
  }

  // 캠페인 상담 신청 수 증가
  const incrementConsultations = async (campaignId) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)

      if (campaignDoc.exists()) {
        const currentConsultations = campaignDoc.data().consultations || 0
        await updateDoc(campaignRef, {
          consultations: currentConsultations + 1
        })
      }
    } catch (err) {
      console.error('상담 수 업데이트 실패:', err)
    }
  }

  // 캠페인 상태 확인 (활성/만료)
  const getCampaignStatus = (campaign) => {
    if (campaign.status === 'expired') return 'expired'

    const { date: today } = getKoreanTime()
    if (campaign.endDate < today) return 'expired'

    return 'active'
  }

  // 남은 일수 계산
  const getRemainingDays = (endDate) => {
    const { fullDate: today } = getKoreanTime()
    const end = new Date(endDate)
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24))
    return Math.max(0, diff)
  }

  // 캠페인에 연결된 광고 조회 (인앱/팝업/스플래시)
  const getLinkedAds = async (campaignId) => {
    const linkedAds = {
      adBanners: [],
      popupAds: [],
      splashAds: []
    }

    try {
      // 인앱 광고 조회
      const adBannerQuery = query(
        collection(db, 'adBanners'),
        where('campaignId', '==', campaignId)
      )
      const adBannerSnapshot = await getDocs(adBannerQuery)
      linkedAds.adBanners = adBannerSnapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }))

      // 팝업 광고 조회
      const popupQuery = query(
        collection(db, 'popupAds'),
        where('campaignId', '==', campaignId)
      )
      const popupSnapshot = await getDocs(popupQuery)
      linkedAds.popupAds = popupSnapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }))

      // 스플래시 광고 조회
      const splashQuery = query(
        collection(db, 'splashAds'),
        where('campaignId', '==', campaignId)
      )
      const splashSnapshot = await getDocs(splashQuery)
      linkedAds.splashAds = splashSnapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }))

      return linkedAds
    } catch (err) {
      console.error('연결된 광고 조회 실패:', err)
      return linkedAds
    }
  }

  // 캠페인 삭제 (활성 캠페인만 삭제 가능, 환불 없음, 연결된 광고도 함께 삭제)
  const deleteCampaign = async (campaignId, deleteLinkedAds = true) => {
    if (!user.value) return { success: false, error: '로그인이 필요합니다.' }

    try {
      loading.value = true

      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)

      if (!campaignDoc.exists()) {
        return { success: false, error: '캠페인을 찾을 수 없습니다.' }
      }

      const campaign = campaignDoc.data()

      // 본인 캠페인인지 확인
      if (campaign.partnerId !== user.value.uid) {
        return { success: false, error: '본인의 캠페인만 삭제할 수 있습니다.' }
      }

      // 배치 삭제
      const batch = writeBatch(db)

      // 연결된 광고 삭제 (옵션)
      if (deleteLinkedAds) {
        const linkedAds = await getLinkedAds(campaignId)

        // 인앱 광고 삭제
        for (const ad of linkedAds.adBanners) {
          batch.delete(doc(db, 'adBanners', ad.id))
        }

        // 팝업 광고 삭제
        for (const ad of linkedAds.popupAds) {
          batch.delete(doc(db, 'popupAds', ad.id))
        }

        // 스플래시 광고 삭제
        for (const ad of linkedAds.splashAds) {
          batch.delete(doc(db, 'splashAds', ad.id))
        }
      }

      // 캠페인 삭제
      batch.delete(campaignRef)

      // 배치 실행
      await batch.commit()

      // 목록에서 제거
      myCampaigns.value = myCampaigns.value.filter(c => c.id !== campaignId)

      return { success: true }
    } catch (err) {
      console.error('캠페인 삭제 실패:', err)
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 캠페인 기간 연장
  const extendCampaign = async (campaignId, extensionKey) => {
    if (!user.value) return { success: false, error: '로그인이 필요합니다.' }

    const extensionInfo = EXTENSION_PRICING[extensionKey]
    if (!extensionInfo) return { success: false, error: '유효하지 않은 연장 기간입니다.' }

    try {
      loading.value = true

      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)

      if (!campaignDoc.exists()) {
        return { success: false, error: '캠페인을 찾을 수 없습니다.' }
      }

      const campaign = campaignDoc.data()

      // 본인 캠페인인지 확인
      if (campaign.partnerId !== user.value.uid) {
        return { success: false, error: '본인의 캠페인만 연장할 수 있습니다.' }
      }

      // 활성 캠페인인지 확인
      const { date: today } = getKoreanTime()
      if (campaign.endDate < today) {
        return { success: false, error: '만료된 캠페인은 연장할 수 없습니다.' }
      }

      // 코인 차감
      const pointResult = await usePoints(
        extensionInfo.price,
        `캠페인 연장 - ${campaign.propertyName} (${extensionInfo.label})`
      )

      if (!pointResult.success) {
        return { success: false, error: pointResult.error }
      }

      // 종료일 계산
      const currentEndDate = new Date(campaign.endDate)
      currentEndDate.setDate(currentEndDate.getDate() + extensionInfo.days)

      const formatDate = (date) => {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }

      // 캠페인 업데이트
      await updateDoc(campaignRef, {
        endDate: formatDate(currentEndDate),
        expiresAt: currentEndDate.toISOString(),
        price: campaign.price + extensionInfo.price, // 총 지불 금액 누적
        updatedAt: new Date().toISOString()
      })

      // 로컬 목록 업데이트
      const idx = myCampaigns.value.findIndex(c => c.id === campaignId)
      if (idx !== -1) {
        myCampaigns.value[idx].endDate = formatDate(currentEndDate)
        myCampaigns.value[idx].price = campaign.price + extensionInfo.price
      }

      return {
        success: true,
        newEndDate: formatDate(currentEndDate),
        daysAdded: extensionInfo.days
      }
    } catch (err) {
      console.error('캠페인 연장 실패:', err)
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  return {
    myCampaigns,
    loading,
    error,
    getPricing,
    getExtensionPricing,
    hasActiveCampaign,
    getActiveCampaign,
    createCampaign,
    loadMyCampaigns,
    getCampaignByCode,
    incrementViews,
    incrementConsultations,
    getCampaignStatus,
    getRemainingDays,
    getLinkedAds,
    deleteCampaign,
    extendCampaign
  }
}
